import { useMaskedValue } from '@lemonade-hq/cdk';
import { randomUuid } from '@lemonade-hq/ts-helpers';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import type { RefObject } from 'react';
import { forwardRef, useCallback, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';
import type { FormatCurrencyProps } from '../../hooks/useFormatCurrency';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';
import { baseInput } from '../BaseComponents.css';
import type { textTransformVariants } from '../BaseComponents.css';
import { Flex } from '../Flex/Flex';
import type { IconName } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import * as styles from './Input.css';

export interface RightIcon {
  readonly name: IconName;
  readonly onClick: () => void;
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  readonly icon?: IconName;
  readonly rightIcon?: RightIcon;
  readonly onClear?: () => void;
  readonly textTransform?: keyof typeof textTransformVariants;
  readonly hasError?: boolean;
  readonly floatingLabel?: boolean;
  readonly mask?: string;
  readonly persistMask?: string;
  readonly formatCurrency?: boolean;
  readonly formatCurrencyProps?: FormatCurrencyProps;
}

const getRefId = (idRef: RefObject<string>): string => {
  return `input_${idRef.current}`;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      placeholder,
      icon,
      rightIcon,
      onClear,
      disabled,
      textTransform,
      hasError,
      floatingLabel = true,
      value,
      onChange,
      mask,
      persistMask,
      formatCurrency = false,
      formatCurrencyProps,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const showIcon = icon != null;
    const showClearButton = onClear != null && Boolean(value) && !disabled;
    const idRef = useRef(randomUuid());
    const showPersistentMask = persistMask != null;

    const { onChangeWithRemoveFormatting, formattedValue } = useFormatCurrency({
      value,
      onChange,
      inputRef: internalRef,
      disableFormat: !formatCurrency,
      formattingProps: formatCurrencyProps,
    });

    const { maskedValue, onChangeWithMask } = useMaskedValue({
      value: formattedValue?.toString(),
      mask,
      onChange: onChangeWithRemoveFormatting,
    });

    const clearInput = useCallback((): void => {
      onClear?.();

      internalRef.current?.focus();
    }, [onClear, internalRef]);

    return (
      <Flex position="relative">
        {showIcon && (
          <Flex className={styles.icon}>
            <Icon name={icon} variant={disabled ? 'disabled' : 'secondary'}></Icon>
          </Flex>
        )}
        <AnimatePresence>
          {/* TODO: modify onClear API to use the rightIcon pattern */}
          {showClearButton && (
            <motion.div
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key="clear"
              transition={{ duration: 0.1 }}
            >
              <button className={styles.rightButton} disabled={disabled} onClick={clearInput} type="button">
                <Icon name="close" size="md" variant="secondary" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {rightIcon && (
            <motion.div
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key={rightIcon.name}
              transition={{ duration: 0.1 }}
            >
              <button className={styles.rightButton} disabled={disabled} onClick={rightIcon.onClick} type="button">
                <Icon name={rightIcon.name} size="md" variant="secondary" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        <input
          aria-label={placeholder}
          className={clsx(
            styles.input({
              showIcon,
              textTransform,
              floatingLabel,
              showPersistentMask,
            }),
            baseInput({ hasError: hasError }),
            className,
          )}
          disabled={disabled}
          id={getRefId(idRef)}
          onChange={onChangeWithMask}
          ref={mergeRefs([internalRef, ref])}
          value={maskedValue}
          {...props}
          // using placeholder to enable placeholder-shown css selector
          placeholder={placeholder ?? ''}
        />
        <Text
          as="label"
          className={styles.label({
            showIcon,
            hasError,
            floatingLabel,
            showPersistentMask,
          })}
          color={disabled ? '$textDisabled' : '$textTertiary'}
          htmlFor={getRefId(idRef)}
          type="bodyMedium"
        >
          {placeholder}
        </Text>
        {showPersistentMask && floatingLabel && (
          <Text
            as="span"
            className={styles.persistentMask({
              showPersistentMask,
              showIcon: showIcon,
            })}
            type="bodyMedium"
          >
            <Text as="span" style={{ visibility: 'hidden' }} type="bodyMedium">
              {maskedValue}
            </Text>
            {persistMask.slice(maskedValue?.length ?? 0)}
          </Text>
        )}
      </Flex>
    );
  },
);
